import { Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider } from './auth/AuthContext'
import { RequireAuth } from './auth/RequireAuth'
import { AdminRoute } from './auth/ProtectedRoute'
import { AppShell, ScrollToTop } from './shell'
import { SupportChatWidget } from './components/SupportChatWidget'
import { lazyNamed } from './lib/lazyComponent'

// Auth pages
import { LoginPage } from './auth/LoginPage'
import { SignupPage } from './auth/SignupPage'

// Eager: critical landing/auth/dashboard paths only
import { HomePage } from './pages/HomePage'
import { DashboardPage } from './dashboard/DashboardPage'

// Code-split: secondary marketing + protected pages — pulled into route-specific chunks
const FeaturesPage = lazyNamed(() => import('./pages/FeaturesPage'), 'FeaturesPage')
const PricingPage = lazyNamed(() => import('./pages/PricingPage'), 'PricingPage')
const AboutPage = lazyNamed(() => import('./pages/AboutPage'), 'AboutPage')
const ContactPage = lazyNamed(() => import('./pages/ContactPage'), 'ContactPage')
const FAQPage = lazyNamed(() => import('./pages/FAQPage'), 'FAQPage')
const DocsPage = lazyNamed(() => import('./pages/DocsPage'), 'DocsPage')
const BlogPage = lazyNamed(() => import('./pages/BlogPage'), 'BlogPage')
const BlogPostPage = lazyNamed(() => import('./pages/BlogPostPage'), 'BlogPostPage')
const PrivacyPage = lazyNamed(() => import('./pages/PrivacyPage'), 'PrivacyPage')
const TermsPage = lazyNamed(() => import('./pages/TermsPage'), 'TermsPage')
const FreeTrialPage = lazyNamed(() => import('./pages/FreeTrialPage'), 'FreeTrialPage')
const ChainDetailPage = lazyNamed(() => import('./pages/ChainDetailPage'), 'ChainDetailPage')
const CarbonCertificatePage = lazyNamed(() => import('./pages/CarbonCertificatePage'), 'CarbonCertificatePage')
const ComparisonPage = lazyNamed(() => import('./pages/ComparisonPage'), 'ComparisonPage')
const WalletPage = lazyNamed(() => import('./pages/WalletPage'), 'WalletPage')
const SettingsPage = lazyNamed(() => import('./pages/SettingsPage'), 'SettingsPage')
const SecurityPage = lazyNamed(() => import('./pages/SecurityPage'), 'SecurityPage')
const ProtectionPage = lazyNamed(() => import('./pages/ProtectionPage'), 'ProtectionPage')

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center text-white/60">
    <div className="animate-pulse">Loading…</div>
  </div>
)

// The route table. Exported so tests can mount it in a MemoryRouter; the
// sitemap generator (scripts/generate-sitemap.js) reads these <Route>s too.
export function AppRoutes() {
  return (
    <Routes>
      {/* Public marketing pages */}
      <Route path="/" element={<HomePage />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/docs" element={<DocsPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/compare" element={<ComparisonPage />} />
      <Route path="/chain/:id" element={<ChainDetailPage />} />

      {/* Auth */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/free-trial" element={<FreeTrialPage />} />
      {/* Public certificate page — no auth, standalone layout */}
      <Route path="/carbon/:wallet" element={<CarbonCertificatePage />} />

      {/* ADM-131 — RequireAuth layout; REV0721-3 — AdminRoute on admin-only surfaces */}
      <Route element={<RequireAuth />}>
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/wallet" element={<WalletPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/protection" element={<ProtectionPage />} />
        <Route
          path="/security"
          element={
            <AdminRoute>
              <SecurityPage />
            </AdminRoute>
          }
        />
      </Route>

      {/* Legacy redirects */}
      <Route path="/home"        element={<Navigate to="/dashboard" replace />} />
      <Route path="/admin"       element={<Navigate to="/dashboard" replace />} />
      <Route path="/builder"     element={<Navigate to="/dashboard" replace />} />
      <Route path="/trader"      element={<Navigate to="/dashboard" replace />} />
      <Route path="/user"        element={<Navigate to="/dashboard" replace />} />
      <Route path="/analytics"   element={<Navigate to="/dashboard" replace />} />
      <Route path="/profile"     element={<Navigate to="/settings" replace />} />
      <Route path="/portfolio"   element={<Navigate to="/dashboard" replace />} />
      <Route path="/trading"     element={<Navigate to="/dashboard" replace />} />
      <Route path="/builds"      element={<Navigate to="/dashboard" replace />} />
      <Route path="/performance" element={<Navigate to="/dashboard" replace />} />

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <ScrollToTop />
        <AppShell>
          <Suspense fallback={<RouteFallback />}>
          <AppRoutes />
          </Suspense>
        </AppShell>
      </Router>
      <SupportChatWidget />
    </AuthProvider>
  )
}

export default App
